import { forwardRef } from "react";
import { NumberInput, type NumberInputProps } from "./NumberInput";

export interface CurrencyInputProps extends Omit<NumberInputProps, "precisionOptions" | "hideControls" | "step"> {}

/**
 * A specialized input component for currency/monetary values.
 *
 * Features:
 * - Automatically formats values to 2 decimal places on blur
 * - Debounced solidification after 300ms of no typing (triggers onChange)
 * - Validates decimal precision during typing (max 2 decimal places)
 * - Hides browser number input spin buttons
 * - Manages internal display state vs external numeric value
 * - Only exposes finalized numeric values via onChange/onBlur callbacks
 *
 * @param onChange - Callback fired when the value changes (receives numeric value)
 * @param onBlur - Standard blur event handler (receives original event)
 * @param value - The current numeric value for controlled usage
 * @param defaultValue - The default numeric value for uncontrolled usage
 * @param className - Additional CSS classes to apply
 * @param ...props - All other Input component props (except type, step, onChange)
 *
 * @example
 * ```tsx
 * // Controlled usage
 * const [amount, setAmount] = useState(0);
 * <CurrencyInput
 *   value={amount}
 *   onChange={setAmount}
 *   placeholder="Enter amount"
 * />
 *
 * // Uncontrolled usage
 * <CurrencyInput
 *   defaultValue={100.50}
 *   onChange={(value) => console.log('Final value:', value)}
 * />
 * ```
 * @note
 * Future Features:
 * - TODO: support leading currency symbols
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>((props, ref) => {
  return <NumberInput {...props} ref={ref} precisionOptions={{ fixedDecimalDigitCount: 2 }} hideControls={true} />;
});

CurrencyInput.displayName = "CurrencyInput";
